import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, ShieldAlert, Smartphone, Copy, Check, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/adminApi';
import { useAdminAuth } from '../../lib/AdminAuthContext';

export default function AdminSecurity() {
  const { admin, refresh } = useAdminAuth();
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null); // { secret, uri, qr_data_url }
  const [verifyCode, setVerifyCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState(false);

  // ── Password change ──
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwTotp, setPwTotp] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  const totpEnabled = !!admin?.totp_enabled;

  async function startSetup() {
    setBusy(true);
    try {
      const data = await adminApi('/admin/auth/2fa/setup', { method: 'POST' });
      setSetup(data);
    } catch (err) {
      toast.error(err.message || 'Setup failed');
    } finally {
      setBusy(false);
    }
  }

  async function verify() {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code from your app');
    setBusy(true);
    try {
      await adminApi('/admin/auth/2fa/verify-setup', {
        method: 'POST',
        body: JSON.stringify({ code: verifyCode.trim() }),
      });
      toast.success('2FA enabled — your admin account is now hardened.');
      setSetup(null);
      setVerifyCode('');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Invalid code');
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    if (!disablePassword || disableCode.length !== 6) return toast.error('Password and 6-digit code required');
    if (!window.confirm('Disable 2FA? Your admin account will be protected by password only.')) return;
    setBusy(true);
    try {
      await adminApi('/admin/auth/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ password: disablePassword, code: disableCode.trim() }),
      });
      toast.success('2FA disabled');
      setDisablePassword('');
      setDisableCode('');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Failed to disable');
    } finally {
      setBusy(false);
    }
  }

  function copySecret() {
    if (!setup?.secret) return;
    navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (pwNew.length < 8) return toast.error('New password must be at least 8 characters');
    if (pwNew !== pwConfirm) return toast.error('New passwords do not match');
    if (pwNew === pwCurrent) return toast.error('New password must differ from current');
    setPwBusy(true);
    try {
      const body = { current_password: pwCurrent, new_password: pwNew };
      if (totpEnabled) body.totp_code = pwTotp.trim();
      await adminApi('/admin/auth/change-password', { method: 'POST', body: JSON.stringify(body) });
      toast.success('Password updated — keep it safe.');
      setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwTotp('');
    } catch (err) {
      const msg = err.message || '';
      let parsed = null;
      try { parsed = JSON.parse(msg); } catch { /* not json */ }
      if (parsed?.code === 'totp_required') {
        toast.error('Enter your 2FA code to change password');
      } else {
        toast.error(msg || 'Failed');
      }
    } finally {
      setPwBusy(false);
    }
  }

  return (
    <div className="p-8 max-w-2xl" data-testid="admin-security-page">
      <h1 className="text-3xl font-serif font-bold text-white mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Security</h1>
      <p className="text-white/50 text-sm mb-6">Harden your admin sign-in. The same 2FA also protects mobile admin access.</p>

      <div className="bg-[#1A1820] border border-white/10 rounded-2xl p-6">
        <div className="flex items-start gap-4 mb-5">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${totpEnabled ? 'bg-[#2A9D8F]/20' : 'bg-[#E9C46A]/20'}`}>
            {totpEnabled
              ? <ShieldCheck className="w-6 h-6 text-[#2A9D8F]" />
              : <ShieldAlert className="w-6 h-6 text-[#E9C46A]" />}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-white">Two-factor authentication</h2>
            <p className="text-white/60 text-sm mt-1">
              {totpEnabled
                ? 'TOTP is enabled. You\'ll be asked for a 6-digit code on every sign-in.'
                : 'Protect this account with a 6-digit code from Google Authenticator, Authy, or 1Password.'}
            </p>
          </div>
          <span data-testid="admin-2fa-status" className={`px-2 py-1 rounded text-[10px] font-bold tracking-wider ${totpEnabled ? 'bg-[#2A9D8F]/30 text-[#2A9D8F]' : 'bg-[#E9C46A]/30 text-[#E9C46A]'}`}>
            {totpEnabled ? 'ENABLED' : 'DISABLED'}
          </span>
        </div>

        {!totpEnabled && !setup && (
          <button onClick={startSetup} disabled={busy} data-testid="admin-2fa-enable-button" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#C8553D] hover:bg-[#9E3A26] disabled:opacity-60 text-white font-bold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
            Set up 2FA
          </button>
        )}

        {setup && (
          <div className="space-y-4 mt-4 pt-4 border-t border-white/10" data-testid="admin-2fa-setup-panel">
            <p className="text-white/70 text-sm">1. Open your authenticator app and scan this QR code:</p>
            <div className="bg-white p-3 rounded-xl inline-block">
              <img src={setup.qr_data_url} alt="2FA QR code" data-testid="admin-2fa-qr" className="w-44 h-44" />
            </div>
            <details className="text-white/60 text-xs">
              <summary className="cursor-pointer">Can't scan? Enter manually</summary>
              <div className="mt-2 flex items-center gap-2 bg-[#0D0B12] border border-white/10 rounded-lg px-3 py-2 font-mono">
                <code className="flex-1 break-all" data-testid="admin-2fa-secret">{setup.secret}</code>
                <button onClick={copySecret} data-testid="admin-2fa-copy-secret" className="text-[#E07A5F]">
                  {copied ? <Check className="w-4 h-4 text-[#2A9D8F]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </details>
            <p className="text-white/70 text-sm">2. Enter the current 6-digit code shown in your app:</p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength="6"
                data-testid="admin-2fa-verify-input"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123 456"
                className="flex-1 bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-2.5 text-white text-lg font-mono tracking-widest text-center placeholder-white/20 focus:border-[#E07A5F] outline-none"
              />
              <button onClick={verify} disabled={busy || verifyCode.length !== 6} data-testid="admin-2fa-verify-button" className="px-5 py-2.5 rounded-lg bg-[#2A9D8F] hover:bg-[#1f7d72] disabled:opacity-60 text-white font-bold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & enable'}
              </button>
            </div>
          </div>
        )}

        {totpEnabled && (
          <div className="space-y-3 mt-4 pt-4 border-t border-white/10">
            <p className="text-white/60 text-xs">Disable 2FA (requires password + a current code)</p>
            <input
              type="password"
              data-testid="admin-2fa-disable-password"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
              placeholder="Current password"
              className="w-full bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-[#E76F51]"
            />
            <input
              type="text"
              inputMode="numeric"
              maxLength="6"
              data-testid="admin-2fa-disable-code"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              className="w-full bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 outline-none font-mono tracking-widest text-center focus:border-[#E76F51]"
            />
            <button onClick={disable} disabled={busy} data-testid="admin-2fa-disable-button" className="px-5 py-2.5 rounded-lg bg-[#E76F51]/20 hover:bg-[#E76F51]/40 text-[#E76F51] font-bold disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Disable 2FA'}
            </button>
          </div>
        )}
      </div>

      {/* Change password */}
      <div className="bg-[#1A1820] border border-white/10 rounded-2xl p-6 mt-5" data-testid="admin-change-password-card">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#457B9D]/20">
            <KeyRound className="w-6 h-6 text-[#457B9D]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Change password</h2>
            <p className="text-white/60 text-sm mt-1">Rotate the bootstrap password. Use 12+ characters with a mix of letters, numbers, and symbols.</p>
          </div>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-3">
          <input
            type="password"
            autoComplete="current-password"
            data-testid="admin-pw-current"
            value={pwCurrent}
            onChange={(e) => setPwCurrent(e.target.value)}
            placeholder="Current password"
            className="w-full bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-[#E07A5F]"
          />
          <input
            type="password"
            autoComplete="new-password"
            data-testid="admin-pw-new"
            value={pwNew}
            onChange={(e) => setPwNew(e.target.value)}
            placeholder="New password (8+ characters)"
            className="w-full bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-[#E07A5F]"
          />
          <input
            type="password"
            autoComplete="new-password"
            data-testid="admin-pw-confirm"
            value={pwConfirm}
            onChange={(e) => setPwConfirm(e.target.value)}
            placeholder="Confirm new password"
            className="w-full bg-[#0D0B12] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-[#E07A5F]"
          />
          {totpEnabled && (
            <input
              type="text"
              inputMode="numeric"
              maxLength="6"
              data-testid="admin-pw-totp"
              value={pwTotp}
              onChange={(e) => setPwTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="2FA code (6 digits)"
              className="w-full bg-[#0D0B12] border border-[#E9C46A]/40 rounded-lg px-4 py-2.5 text-white placeholder-white/30 outline-none focus:border-[#E9C46A] font-mono tracking-widest text-center"
            />
          )}
          <button type="submit" disabled={pwBusy} data-testid="admin-pw-submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#457B9D] hover:bg-[#355d78] disabled:opacity-60 text-white font-bold">
            {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Update password
          </button>
        </form>
      </div>
    </div>
  );
}
